import { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Chip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { getSolicitation, createSolicitation } from "../services/api";
import { ICreateSolicitation, ISolicitation } from "../lib/types";
import { toast } from "react-toastify";
import useStore from "../store/useStore";
import { getLogoBase64 } from "../utils/themeManager";
import { FullScreenModal } from "../components/FullScreenModal";
import { CameraFullscreen } from "../components/CameraFullscreen";
import { sanitizeHtml } from "../lib/sanitize";
 
function ImageGallery({
  anexos,
  onSelectImage,
}: {
  anexos: ISolicitation["anexos"];
  onSelectImage?: (index: number) => void;
}) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const swiperRef = useRef<any>(null);

  if (!anexos?.length) return null;

  const imageAnexos = anexos;

  if (!imageAnexos.length) return null;

  useEffect(() => {
    if (onSelectImage && swiperRef.current) {
      swiperRef.current.slideTo(currentImageIndex);
    }
  }, [currentImageIndex]);

  return (
    <div className="w-full mb-6" id="image-gallery">
      <div className="relative flex flex-col items-center">
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={10}
          slidesPerView={1}
          navigation={false}
          pagination={{ clickable: true, el: ".custom-swiper-pagination" }}
          onSlideChange={(swiper) => setCurrentImageIndex(swiper.activeIndex)}
          className="w-full"
          onSwiper={(swiper) => (swiperRef.current = swiper)}
        >
          {imageAnexos.map((anexo) => (
            <SwiperSlide
              key={anexo.id}
              onClick={() => {
                onSelectImage?.(anexo as any);
              }}
            >
              <div className="relative w-full h-64">
                <img
                  src={anexo.file.url}
                  alt={anexo.file.label}
                  className="w-full h-64 object-cover rounded-lg"
                />
                {/* Setas customizadas */}
                <button
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white shadow rounded-full p-2 border border-default-200 hover:bg-primary/70 hover:text-secondary transition"
                  onClick={(e) => {
                    e.stopPropagation();
                    swiperRef.current?.slidePrev();
                  }}
                  aria-label="Anterior"
                >
                  <Icon icon="lucide:chevron-left" className="text-xl" />
                </button>
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white shadow rounded-full p-2 border border-default-200 hover:bg-primary/70 hover:text-secondary transition"
                  onClick={(e) => {
                    e.stopPropagation();
                    swiperRef.current?.slideNext();
                  }}
                  aria-label="Próxima"
                >
                  <Icon icon="lucide:chevron-right" className="text-xl" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-3 py-2 rounded-b-lg text-center truncate">
                  {anexo.file.label}
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        {/* Paginação customizada */}
        <div className="custom-swiper-pagination flex justify-center mt-3" />
      </div>
      <div className="text-center mt-2 text-gray-500">
        {currentImageIndex + 1} / {imageAnexos.length}
      </div>
    </div>
  );
}

function MessageThread({
  thread,
  isAlert,
  onSelectImage,
}: {
  thread: ISolicitation[];
  isAlert: boolean;
  onSelectImage?: (id: string) => void;
}) {
  function formatShortDate(dateStr: string) {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return d
        .toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
        .replace(",", " -");
    }
    return dateStr;
  }

  return (
    <div
      className={`flex flex-col gap-4 mt-6 ${isAlert ? "bg-warning-50 rounded-lg p-3" : ""
        }`}
    >
      {thread.map((msg, index) => (
        <div key={msg.id} className="flex flex-col">
          <div
            className={`relative px-4 py-2 rounded-2xl text-sm bg-white border text-gray-800 max-w-[calc(100%-1.5rem)] overflow-hidden ${index === 0
              ? "border-primary bg-primary text-secondary"
              : "border-default-200"
              }`}
          >
            {msg.assunto && (
              <>
                <div className="text-sm font-semibold mb-1">
                  Comunicação atendida
                </div>
                <div className="font-semibold mb-2">{msg.assunto}</div>
              </>
            )}
            {msg.empresaNome && (
              <div className="text-xs text-gray-500 mb-2">{msg.empresaNome}</div>
            )}
            <div className="break-words" dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.mensagem) }}></div>
            {msg.anexos && msg.anexos.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="text-xs text-gray-500 mb-2">Anexos: </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-w-[600px]">
                  {msg.anexos.map((anexo) => {
                    const ext = anexo.file.extension?.toLowerCase() || "";
                    let iconName = "lucide:file-text";
                    let label = ext.toUpperCase();

                    if (ext === "pdf") {
                      iconName = "lucide:file";
                    } else if (["xlsx", "xls"].includes(ext)) {
                      iconName = "lucide:file-spreadsheet";
                    } else if (["doc", "docx"].includes(ext)) {
                      iconName = "lucide:file-text";
                    } else if (["zip", "rar", "7z"].includes(ext)) {
                      iconName = "lucide:file-archive";
                    }

                    return (
                      <div
                        key={anexo.id}
                        onClick={(e) => {
                          e.preventDefault();
                          if (onSelectImage) {
                            onSelectImage(anexo as any);
                          }
                        }}
                        className="relative group cursor-pointer rounded-lg overflow-hidden border border-default-200 aspect-[4/3] bg-gray-100"
                      >
                        {["jpg", "jpeg", "png", "gif", "webp"].includes(ext) ? (
                          <img
                            src={anexo.file.url}
                            alt={anexo.file.label}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center w-full h-full p-3">
                            {/* Container do ícone com estilo minimalista */}
                            <div className="relative mb-2">
                              {/* Ícone principal com cor neutra */}
                              <Icon
                                icon={iconName}
                                className="w-10 h-10 text-gray-500 group-hover:text-gray-700 transition-colors duration-200"
                              />

                              {/* Badge da extensão discreto */}
                              <span className="absolute -bottom-1.5 -right-1.5 bg-white px-1.5 py-0.5 rounded-full text-[10px] font-medium text-gray-600 shadow-xs border border-gray-200">
                                {label}
                              </span>
                            </div>

                            {/* Nome do arquivo (opcional) - mais compacto */}
                            {anexo.file.label && (
                              <span className="text-xs text-gray-600 text-center max-w-full truncate">
                                {anexo.file.label}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                          <div className="text-white opacity-0 group-hover:opacity-100 transition-all text-center p-1">
                            <Icon icon="lucide:zoom-in" className="text-base" />
                          </div>
                        </div>
                      </div>
                    );
                  })}


                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
            <span>{formatShortDate(msg.created_at)}</span>
            <span>•</span>
            <span className="flex items-center">
              {msg.origem === thread[0].origem ? "Você" : msg.origem}
              <Icon icon="lucide:arrow-right" className="inline-block mx-1" />
              {msg.destino}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function MessageReplyBox({
  onSend,
  disabled,
}: {
  onSend: (msg: string, files?: File[]) => void;
  disabled?: boolean;
}) {
  const [input, setInput] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const handleSend = () => {
    if (input.trim() || selectedFiles.length > 0) {
      onSend(input, selectedFiles);
      setInput("");
      setSelectedFiles([]);
    }
  };

  const handleSendAndRedirect = () => {
    if (input.trim() || selectedFiles.length > 0) {
      handleSend();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setSelectedFiles((prev) => [...prev, ...newFiles]);
      // Reset the input value so the same file can be selected again
      e.target.value = "";
    }
    setShowAttachMenu(false);
  };

  const handleCameraCapture = (
    image:
      | File
      | {
        name: string;
        originalName: string;
        uri: string;
        type: string;
        editing: boolean;
      }
  ) => {
    if ("uri" in image) {
      // Convert the image object to a File object
      fetch(image.uri)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], image.name, { type: image.type });
          setSelectedFiles((prev) => [...prev, file]);
        })
        .catch((err) => {
          console.error("Erro ao processar imagem da câmera:", err);
        });
    } else {
      // It's already a File
      setSelectedFiles((prev) => [...prev, image]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const triggerGalleryInput = () => {
    galleryInputRef.current?.click();
  };

  const openCamera = () => {
    setCameraOpen(true);
    setShowAttachMenu(false);
  };

  return (
    <>
      {/* Camera Fullscreen Component */}
      <CameraFullscreen
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
      />

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-default-200 shadow z-30 px-4 py-3 flex flex-col gap-2 max-w-2xl mx-auto pb-safe-6">
        {/* Selected files preview */}
        {selectedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2 max-h-24 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="relative bg-gray-100 rounded p-1 flex items-center gap-1"
              >
                <Icon
                  icon={
                    file.type.startsWith("image/")
                      ? "lucide:image"
                      : "lucide:file"
                  }
                  className="text-gray-500"
                />
                <span className="text-xs truncate max-w-[100px]">
                  {file.name}
                </span>
                <button
                  onClick={() => handleRemoveFile(index)}
                  className="text-gray-500 hover:text-red-500"
                >
                  <Icon icon="lucide:x" className="text-sm" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input area */}
        <div className="flex gap-2 w-full">
          <div className="relative">
            <Button
              isIconOnly
              variant="light"
              onPress={() => setShowAttachMenu(!showAttachMenu)}
              aria-label="Anexar"
              isDisabled={disabled}
            >
              <Icon icon="lucide:paperclip" className="text-lg text-gray-500" />
            </Button>

            {/* Attachment menu */}
            {showAttachMenu && (
              <div className="absolute bottom-full left-0 mb-2 bg-white rounded-lg shadow-lg border border-default-200 p-2 flex flex-col gap-2 min-w-[150px]">
                <button
                  onClick={triggerFileInput}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-md"
                >
                  <Icon icon="lucide:file" />
                  <span>Arquivo</span>
                </button>
                <button
                  onClick={openCamera}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-md"
                >
                  <Icon icon="lucide:camera" />
                  <span>Câmera</span>
                </button>
                <button
                  onClick={triggerGalleryInput}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-md"
                >
                  <Icon icon="lucide:image" />
                  <span>Galeria</span>
                </button>
              </div>
            )}

            {/* Hidden file inputs */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              multiple
              accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            />
            <input
              type="file"
              ref={galleryInputRef}
              onChange={handleFileSelect}
              className="hidden"
              multiple
              accept="image/*"
            />
          </div>

          <input
            className="flex-1 px-3 py-2 rounded-full border border-default-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Responder..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={disabled}
          />

          <Button
            color="primary"
            isIconOnly
            onPress={handleSendAndRedirect}
            aria-label="Enviar"
            isDisabled={
              disabled || (input.trim() === "" && selectedFiles.length === 0)
            }
          >
            <Icon icon="lucide:send" className="text-lg text-secondary" />
          </Button>
        </div>
      </div>
    </>
  );
}

export function MessageDetailsPage() {
  const history = useHistory();
  const location: any = useLocation();
  const { id, empresaId, companyName, selected } = location.state || {};
  const [thread, setThread] = useState<ISolicitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnexo, setSelectedAnexo] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingRead, setConfirmingRead] = useState(false);
  const store = useStore();
  const { authUser, selectedCompany } = store;
  const status = thread[0]?.status?.toLowerCase() || "pendente";
  const needResponse = selected === 'inbox' ? thread[0]?.resposta !== "0" : true;

  useEffect(() => {
    if (!id || !empresaId) {
      history.replace("/messages");
      return;
    }

    loadThread();
  }, [id, empresaId]);

  const loadThread = async () => {
    if (!id || !empresaId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getSolicitation(id, empresaId);
      // Get the main message and filter it from the thread
      const mainMessage = data.find((msg) => msg.id === id);
      if (!mainMessage) {
        setError("Mensagem não encontrada");
        return;
      }
      setThread([mainMessage, ...data.filter((msg) => msg.id !== id)]);
    } catch (err) {
      setError("Erro ao carregar as mensagens");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (msg: string, files?: File[]) => {
    try {
      setLoading(true);

      let payload: ICreateSolicitation = {
        empresa_id: selectedCompany!.id,
        mensagem: msg,
        files: files || [],
        id_destinatario: authUser?.bpo!.id,
        destino: authUser?.bpo!.empresa!,
        tipo_destinatario: "cliente",
        id_origem: authUser?.contato!.id,
        origem: authUser?.contato!.nome!,
        tipo_origem: "contato",
        resposta: 0,
        via_app: 1,
        id_solicitacao: id,
      };

      await createSolicitation(payload);
      await loadThread(); // Recarrega a thread após enviar

      toast.success("Mensagem enviada com sucesso", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
      });
      history.push("/messages");
    } catch (err: any) {
      console.error("Erro ao enviar mensagem:", err);
      toast.error(err?.message || "Erro ao enviar mensagem", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectImage = (anexo: any) => {
    setSelectedAnexo(anexo);
  };

  const handleCloseModal = () => {
    setSelectedAnexo(null);
  };

  const handleConfirmReading = async () => {
    try {
      setConfirmingRead(true);
      
      let payload: ICreateSolicitation = {
        empresa_id: selectedCompany!.id,
        mensagem: "Leitura confirmada",
        files: [],
        id_destinatario: authUser?.bpo!.id,
        destino: authUser?.bpo!.empresa!,
        tipo_destinatario: "cliente",
        id_origem: authUser?.contato!.id,
        origem: authUser?.contato!.nome!,
        tipo_origem: "contato",
        resposta: 0,
        via_app: 1,
        id_solicitacao: id,
      };

      await createSolicitation(payload);
      
      toast.success("Leitura confirmada com sucesso", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
      });
      
      history.push("/messages", { selected });
    } catch (err) {
      console.error("Erro ao confirmar leitura:", err);
      toast.error("Erro ao confirmar leitura", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
      });
    } finally {
      setConfirmingRead(false);
    }
  };

  if (!id || !empresaId) {
    return null;
  }

  // Get all attachments from all messages for the gallery
  const allAttachments = thread.reduce((acc, msg) => {
    if (msg.anexos) {
      acc?.push(...msg.anexos);
    }
    return acc;
  }, [] as ISolicitation["anexos"]);

  return (
    <>
      <FullScreenModal
        isOpen={selectedAnexo !== null}
        onClose={handleCloseModal}
        src={selectedAnexo?.file?.url || ""}
        type={selectedAnexo?.file?.type?.includes("image") ? "image" : "file"}
        name={selectedAnexo?.file?.label || ""}
        extension={selectedAnexo?.file?.extension}
      />
      <div className="min-h-screen bg-white flex flex-col">
        <div className="bg-primary pt-4 pb-4 px-4">
          <div className="flex flex-col max-w-2xl mx-auto">
            <div className="flex items-center">
              <Button
                isIconOnly
                variant="light"
                onPress={() =>
                  history.push("/messages", {
                    selected: location?.state?.selected,
                  })
                }
              >
                <Icon icon="lucide:arrow-left" className="text-secondary text-xl" />
              </Button>
              <div className="flex items-center gap-3 flex-1">
                <div className="rounded-full w-12 h-12 border border-white flex items-center justify-center overflow-hidden">
                  <img
                    src={getLogoBase64()}
                    alt="Logo"
                    className="max-w-full max-h-full object-contain" // Mantém proporção e tamanho natural
                  />
                </div>

                <div className="flex flex-col flex-1">
                  <span className="text-secondary text-base font-bold">
                    {authUser?.contato?.nome}
                  </span>
                  <span className="text-secondary text-sm font-semibold flex items-center max-w-full overflow-hidden">
                    <span className="max-w-[30%] truncate mr-2">
                      {companyName}
                    </span>
                    <span className="text-secondary mr-2">/</span>
                    <span className="text-secondary whitespace-nowrap">
                      Mensagem
                    </span>
                  </span>
                </div>
              </div>
              <div className="w-10"></div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pb-28">
          <div className="max-w-2xl mx-auto px-4 py-6 overflow-scroll max-h-[calc(100vh-10rem)]">
            {/* Linha: Assunto + Status */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-base font-semibold text-gray-900 truncate">
                {thread[0]?.assunto}
              </span>
              <Chip
                color={status === "pendente" ? "warning" : "success"}
                size="sm"
                className="ml-2"
              >
                {status === "pendente" ? "Pendente" : "Respondido"}
              </Chip>
            </div>

            {/* Galeria de imagens */}
            {!loading &&
              thread.length > 0 &&
              allAttachments &&
              allAttachments.filter((item) =>
                ["jpg", "jpeg", "png", "gif", "webp"].includes(
                  item.file.extension?.toLowerCase() || ""
                )
              ).length > 0 && (
                <ImageGallery
                  anexos={allAttachments.filter((item) =>
                    ["jpg", "jpeg", "png", "gif", "webp"].includes(
                      item.file.extension?.toLowerCase() || ""
                    )
                  )}
                  onSelectImage={handleSelectImage}
                />
              )}

            {/* Estado de carregamento */}
            {loading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            )}

            {/* Mensagem de erro */}
            {error && (
              <div className="text-center py-8 text-red-500">
                <Icon icon="lucide:alert-circle" className="text-3xl mb-2" />
                <p>{error}</p>
                <Button
                  color="primary"
                  variant="light"
                  onPress={loadThread}
                  className="mt-2"
                >
                  Tentar novamente
                </Button>
              </div>
            )}

            {/* Thread de mensagens */}
            {!loading && !error && (
              <MessageThread
                thread={thread}
                isAlert={false}
                onSelectImage={handleSelectImage}
              />
            )}
          </div>
        </div>

        {/* Caixa de resposta ou botão de confirmar leitura */}
        {needResponse ? (
          <MessageReplyBox onSend={handleSendReply} disabled={loading} />
        ) : (
          <div className="fixed bottom-0 left-0 right-0 bg-primary border-t border-default-200 shadow z-30 px-4 py-3 max-w-2xl pb-safe-8 mx-auto">
            <Button
              color="primary"
              className="w-full text-secondary"
              onPress={handleConfirmReading}
              isLoading={confirmingRead}
              startContent={!confirmingRead && <Icon icon="lucide:check-circle" className="text-lg" />}
            >
              Confirmar leitura
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
